import { style, globalStyle, styleVariants } from '@vanilla-extract/css'
import { vars, focusRing, dot as dotBase } from '../design/theme.css'
import { birdNamePlate, gloss as glossBase, sciName, utility } from '../design/primitives/text.css'

// EOLAÍ — the illustrated species guide, on the design system. Controls are the shared
// SegmentedControl + Field (see JSX); this module owns the grid of plates and the item
// hierarchy. Plates are pictures on paper — no boxes, no chrome — the ring shows on keyboard
// focus only.

// ---- Controls bar -----------------------------------------------------
export const controls = style({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: `${vars.space[3]} ${vars.space[5]}`,
  paddingBottom: vars.space[4],
  borderBottom: `1px solid ${vars.color.rule}`,
})
export const group = style({ display: 'inline-flex', alignItems: 'center', gap: vars.space[2] })
// SHOW / SORT, at the same size as the options they label — they were a size smaller, so the
// control group read as two ranks of type where it is one control.
export const groupLabel = utility
// Right-aligned while the bar is one line; full width once it has wrapped. `margin-left: auto`
// alone put a lone search box against the right edge of a stacked column on a phone, with the
// width of the screen of white to its left — it read as a misplaced element rather than a control.
export const filter = style({
  marginLeft: 'auto',
  minWidth: '180px',
  maxWidth: '240px',
  '@media': {
    'screen and (max-width: 560px)': { marginLeft: 0, minWidth: 0, maxWidth: 'none', width: '100%' },
  },
})

// ---- The plate grid ---------------------------------------------------
export const grid = style({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
  gap: `${vars.space[7]} ${vars.space[5]}`,
  paddingTop: vars.space[6],
  // Wider desktop grid — roomier plates once there's space (brief §7).
  '@media': {
    'screen and (min-width: 1100px)': {
      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: `${vars.space[8]} ${vars.space[6]}`,
    },
  },
})
// The follow mark (.follow-card, still in editorial.css) positions itself in this cell.
export const cell = style({ position: 'relative' })

export const item = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  background: 'transparent',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  textAlign: 'left',
  color: 'inherit',
  font: 'inherit',
  WebkitTapHighlightColor: 'transparent',
  selectors: {
    '&:focus': { outline: 'none' },
    // The house ring, like every other control — this was the one surface with a ring of its
    // own (1.5px, in the rule grey, which barely showed).
    '&:focus-visible': focusRing,
  },
})

export const plate = style({
  width: '100%',
  height: '132px',
  marginBottom: vars.space[3],
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
})
globalStyle(`${plate} img`, {
  maxWidth: '100%',
  maxHeight: '132px',
  objectFit: 'contain',
  display: 'block',
  transition: `filter ${vars.dur.med} ease`,
})

// Conservation mark — a dot and its list name above the name. NOT a pill: no ground, no
// capsule, just a marked label (§4). The row is RESERVED whether or not the bird has a listing,
// so the names line up across a row of plates instead of stepping up and down by a line.
export const consLine = style([
  utility,
  {
    display: 'flex',
    alignItems: 'center',
    gap: vars.space[1],
    minHeight: vars.space[4],
    marginBottom: vars.space[1],
    color: vars.color.ink2,
  },
])
export const pill = style([consLine, { cursor: 'help' }])
export const dot = styleVariants({
  red: { ...dotBase, background: vars.color.red },
  amber: { ...dotBase, background: vars.color.amber },
  green: { ...dotBase, background: vars.color.green },
})

// The plate's title: the shared plate-rank bird name. Composed into a LOCAL class rather than
// aliased, because the hover and un-heard rules below select on it — aliasing would reach every
// plate-rank name in the app.
export const name = style([birdNamePlate, {}])
globalStyle(`${item}:hover ${name}`, {
  textDecoration: 'underline',
  textUnderlineOffset: vars.uline.mark,
})
export const gloss = glossBase
export const sci = style([sciName, { display: 'block', marginTop: vars.space[1] }])

// all-time / today / first-heard on one tight pipe-separated line, mono tabular figures.
// Wraps. It used to be nowrap in both axes, so a long Irish name plus three figures pushed the
// plate past its column on a phone (§18).
export const stats = style({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  marginTop: vars.space[2],
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  fontVariantNumeric: 'tabular-nums',
  color: vars.color.inkSoft,
})
export const stat = style({ display: 'inline-flex', gap: vars.space[1], whiteSpace: 'nowrap' })
// A middle dot, spaced, like every other separator in the publication — this was the one place
// that parted its parts with a pipe (§6/§16).
globalStyle(`${stat} + ${stat}::before`, {
  content: '"·"',
  color: vars.color.inkSoft,
  margin: `0 ${vars.space[2]}`,
})
globalStyle(`${stats} b`, { color: vars.color.ink, fontWeight: 500, fontVariantNumeric: 'tabular-nums' })
export const unseenNote = style({ fontStyle: 'italic', fontFamily: vars.font.serif })

// Un-heard species: greyed plate, still browsable; colour returns on hover. The plate is held
// back rather than half-erased and the NAME keeps a readable grey — at 0.4 opacity and
// --ink-soft the entry was legible only to somebody who already knew it was there (§12).
export const unseen = style({})
globalStyle(`${unseen} ${plate} img`, { filter: 'grayscale(0.9) opacity(0.55)' })
globalStyle(`${unseen}:hover ${plate} img`, { filter: 'none' })
globalStyle(`${unseen} ${name}`, { color: vars.color.ink2 })
